import { useLazyQuery } from "@apollo/client";
import { IonCol, IonGrid, IonRow } from "@ionic/react";
import equal from "deep-equal";
import { ComponentProps, useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import ActionIconButton from "@components/buttons/ActionIconButton";
import PrimaryButton from "@components/buttons/PrimaryButton";
import DeleteConfirmationAlert from "@components/DeleteConfirmationAlert";
import MessageTemplateCategoryFilters from "@components/forms/MessageTemplateCategoryFilters";
import MessageTemplateCategoryForm from "@components/forms/MessageTemplateCategoryForm";
import HandleQuery from "@components/HandleQuery";
import Tag from "@components/labels/Tag";
import styles from "@components/MessageTemplatesTable.module.css";
import Modal, { ModalElement } from "@components/Modal";
import Table, { TableHeader } from "@components/Table";
import BodyCopy from "@components/typography/BodyCopy";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import useOpener from "@hooks/useOpener";
import {
  GetMessageTemplateCategoriesDocument,
  GetMessageTemplateCategoriesQuery,
  GetMessageTemplateCategoriesQueryVariables,
  MessageTemplateCategoriesSortByEnum,
  MessageTemplateCategoryDestroyDocument,
  MessageTemplateCategoryDestroyMutation,
  MessageTemplateCategoryDestroyMutationVariables,
  MessageTemplateCategoryFragment,
  SortDirectionEnum
} from "@typing/Generated";
import { imageUrl } from "@utils/imageUtils";

type Filters = ComponentProps<typeof MessageTemplateCategoryFilters>["filters"];

const MessageTemplateCategoriesTable = () => {
  const destroyOpener = useOpener();
  const categoryOpener = useOpener();
  const intl = useIntl();

  const [getMessageTemplateCategories, getMessageTemplateCategoriesQuery] = useLazyQuery<
    GetMessageTemplateCategoriesQuery,
    GetMessageTemplateCategoriesQueryVariables
  >(GetMessageTemplateCategoriesDocument);

  const [filters, setFilters] = useState<Filters>({
    search: ""
  });

  const [sortBy, setSortBy] = useState<MessageTemplateCategoriesSortByEnum>(MessageTemplateCategoriesSortByEnum.title);
  const [sortDirection, setSortDirection] = useState<SortDirectionEnum>(SortDirectionEnum.descending);
  const [modifyingCategory, setModifyingCategory] = useState<MessageTemplateCategoryFragment | null>(null);

  const [destroyMessageTemplateCategory, client] = useMutationWithValidation<
    MessageTemplateCategoryDestroyMutation,
    MessageTemplateCategoryDestroyMutationVariables
  >(MessageTemplateCategoryDestroyDocument, "messageTemplateCategory", {
    update: () => {
      // This kicks out all of the categories queries so we will re-make them. If you're flipping between filters
      // deleting will update the categories, but switching filters will have the cached value
      client.cache.evict({ fieldName: "messageTemplateCategories", id: "ROOT_QUERY" });
      getMessageTemplateCategoriesQuery.refetch();
    }
  });

  const categoryModalRef = useRef<ModalElement>(null);

  const handleCategoryError = useCallback(() => {
    if (categoryModalRef.current) {
      categoryModalRef.current.scrollToTop();
    }
  }, [categoryModalRef]);

  const handleFilterChange = useCallback(
    (newFilters: Filters) => {
      if (!equal(newFilters, filters)) {
        setFilters(newFilters);
      }
    },
    [filters]
  );

  const handleCategoryEdit = useCallback(
    (messageTemplate: MessageTemplateCategoryFragment) => () => {
      setModifyingCategory(messageTemplate);
      categoryOpener.open();
    },
    [categoryOpener]
  );

  const handleCategoryCreate = () => {
    setModifyingCategory(null);
    categoryOpener.open();
  };

  const handleCategoryDestroy = useCallback(
    (messageTemplate: MessageTemplateCategoryFragment) => () => {
      setModifyingCategory(messageTemplate);
      destroyOpener.open();
    },
    [destroyOpener]
  );

  const handleCategoryDestroyed = useCallback(() => {
    if (modifyingCategory) {
      destroyMessageTemplateCategory({
        id: modifyingCategory.id
      });
    }
  }, [destroyMessageTemplateCategory, modifyingCategory]);

  useEffect(() => {
    getMessageTemplateCategories({
      variables: {
        search: filters.search,
        sortBy,
        sortDirection
      }
    });
  }, [filters, getMessageTemplateCategories, sortBy, sortDirection]);

  const messageTemplateCategories = useNodesFromConnection(
    getMessageTemplateCategoriesQuery.data?.messageTemplateCategories
  );

  const tableHeaders: TableHeader<MessageTemplateCategoriesSortByEnum>[] = [
    { columnSortBy: MessageTemplateCategoriesSortByEnum.title },
    { messageKey: "pages.expert.messageTemplateCategories.tableHeadings.actions" }
  ];

  return (
    <HandleQuery query={getMessageTemplateCategoriesQuery}>
      {data => (
        <>
          <div>
            <IonGrid>
              <IonRow className="ion-align-items-center">
                <IonCol className={styles.filters}>
                  <MessageTemplateCategoryFilters filters={filters} onChange={handleFilterChange} />
                </IonCol>
                <IonCol className="align-right" size="auto">
                  <div className={styles.buttons}>
                    <PrimaryButton
                      onClick={handleCategoryCreate}
                      textKey="pages.expert.messageTemplateCategories.new"
                    />
                  </div>
                </IonCol>
              </IonRow>
            </IonGrid>
          </div>
          <Table<MessageTemplateCategoriesSortByEnum>
            fetchMore={getMessageTemplateCategoriesQuery.fetchMore}
            headerKey="pages.expert.messageTemplateCategories.tableHeadings"
            headers={tableHeaders}
            pageInfo={data?.messageTemplateCategories.pageInfo}
            setSortBy={setSortBy}
            setSortDirection={setSortDirection}
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            {messageTemplateCategories.map(category => (
              <tr key={category.id}>
                <td>
                  <Tag color="secondary-500" text={category.title} />
                </td>
                <td>
                  <ActionIconButton
                    ariaLabelKey="accessibility.ariaLabels.edit"
                    icon="edit"
                    onClick={handleCategoryEdit(category)}
                  />
                  <ActionIconButton
                    ariaLabelKey="accessibility.ariaLabels.delete"
                    disabled={category.messageTemplatesCount !== 0}
                    hoverContent={
                      category.messageTemplatesCount !== 0 ? (
                        <FormattedMessage id="pages.expert.messageTemplateCategories.deleteDisabled" />
                      ) : undefined
                    }
                    icon="trash"
                    onClick={handleCategoryDestroy(category)}
                  />
                </td>
              </tr>
            ))}
          </Table>
          {messageTemplateCategories.length === 0 && (
            <div className={styles.empty}>
              <img
                alt={intl.formatMessage({ id: "accessibility.altTags.manWithGiantMagnifyingGlass" })}
                className={styles.emptyImage}
                src={imageUrl("manWithGiantMagnifyingGlass")}
              />
              <div className={styles.bottomSpacing}>
                <BodyCopy>
                  <FormattedMessage id="pages.expert.messageTemplateCategories.empty" />
                </BodyCopy>
              </div>
            </div>
          )}
          <DeleteConfirmationAlert
            messageKey="pages.expert.messageTemplateCategories.delete"
            onDelete={handleCategoryDestroyed}
            opener={destroyOpener}
          />
          <Modal
            opener={categoryOpener}
            ref={categoryModalRef}
            title={
              modifyingCategory
                ? intl.formatMessage({ id: "pages.expert.messageTemplateCategories.editCategory" })
                : intl.formatMessage({ id: "pages.expert.messageTemplateCategories.addCategory" })
            }
          >
            {({ modalDisplayed }) => (
              <MessageTemplateCategoryForm
                autofocus={modalDisplayed}
                messageTemplateCategory={modifyingCategory}
                onCancel={categoryOpener.close}
                onError={handleCategoryError}
                onSuccess={categoryOpener.close}
              />
            )}
          </Modal>
        </>
      )}
    </HandleQuery>
  );
};

export default MessageTemplateCategoriesTable;
